import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardNavSidebar, {
  NAV_SIDEBAR_COLLAPSED_PX,
  NAV_SIDEBAR_EXPANDED_PX,
  NAV_SIDEBAR_STORAGE_KEY,
} from '../main-dashboard/components/DashboardNavSidebar';
import DashboardTopbar from '../main-dashboard/components/DashboardTopbar';
import UserProfileModal from '../main-dashboard/components/UserProfileModal';
import { useAuth } from '../user-dashboard/auth/AuthContext';
import { useTheme } from '../shared/theme/ThemeContext';
import { pageShellClass } from '../shared/theme/themeStyles';
import type { MapToolRailIcon } from '../shared/components/MapToolRail';
import {
  buildAllowedEntitlementSet,
  getEffectiveAllowedLayerKeysForMap,
} from '../../config/analysisLayerEntitlements';
import { isProjectDeveloper } from './homePath';
import SflmaExplorer from './SflmaExplorer';

export default function CarbonProjectsPage() {
  const navigate = useNavigate();
  const { user, loading, isAuthenticated, refreshUser } = useAuth();
  const { theme } = useTheme();
  const [navMenuExpanded, setNavMenuExpanded] = useState(() => {
    if (typeof window === 'undefined') return true;
    try {
      const saved = window.localStorage.getItem(NAV_SIDEBAR_STORAGE_KEY);
      if (saved === '1') return true;
      if (saved === '0') return false;
    } catch {
      /* storage unavailable */
    }
    return window.innerWidth >= 1280;
  });
  const [settingsOpen, setSettingsOpen] = useState(false);
  const allowedEntitlementSet = useMemo(
    () => buildAllowedEntitlementSet(getEffectiveAllowedLayerKeysForMap(user)),
    [user],
  );

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate('/userdashboard', { replace: true });
      return;
    }
    if (!isProjectDeveloper(user?.role)) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, loading, navigate, user?.role]);

  const toggleNavMenu = () => {
    setNavMenuExpanded((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(NAV_SIDEBAR_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* storage unavailable */
      }
      return next;
    });
  };

  const openMapTool = (icon: MapToolRailIcon) => {
    setSettingsOpen(false);
    if (icon === 'fieldServices') {
      navigate('/?tool=fieldServices');
      return;
    }
    if (icon === 'farm') {
      navigate('/');
      return;
    }
    navigate(`/?tool=${icon}`);
  };

  if (loading || !isAuthenticated || !isProjectDeveloper(user?.role) || !user) {
    return (
      <div className={`flex h-screen items-center justify-center ${pageShellClass(theme)}`}>
        <p className="text-sm text-[#6b7280]">Opening Carbon Projects…</p>
      </div>
    );
  }

  return (
    <div
      className={`relative flex h-screen flex-col overflow-hidden transition-[padding-left] duration-200 ease-out motion-reduce:transition-none lg:pl-[var(--nav-sidebar-w)] ${pageShellClass(theme)}`}
      style={
        {
          '--nav-sidebar-w': `${navMenuExpanded ? NAV_SIDEBAR_EXPANDED_PX : NAV_SIDEBAR_COLLAPSED_PX}px`,
        } as CSSProperties
      }
    >
      <DashboardNavSidebar
        expanded={navMenuExpanded}
        onToggleExpanded={toggleNavMenu}
        activeIcon={null}
        onIconClick={openMapTool}
        onOpenSettings={() => setSettingsOpen(true)}
        settingsActive={settingsOpen}
        allowedEntitlementSet={allowedEntitlementSet}
        showCarbonProjects
        carbonProjectsActive={!settingsOpen}
        onOpenCarbonProjects={() => setSettingsOpen(false)}
      />
      <DashboardTopbar
        sidebarOpen={false}
        onToggleSidebar={() => setSettingsOpen(false)}
        onStartTour={() => undefined}
        onOpenProfile={() => setSettingsOpen(true)}
        activeAnalysisIcon={null}
        allowedEntitlementSet={allowedEntitlementSet}
        onAnalysisIconClick={openMapTool}
        settingsOpen={settingsOpen}
        pageOverride={
          settingsOpen
            ? null
            : {
                title: 'SFLMA decision explorer',
                subtitle: 'Nationwide PIA candidates for a closer look',
              }
        }
        showCarbonProjects
        carbonProjectsActive={!settingsOpen}
        onOpenCarbonProjects={() => setSettingsOpen(false)}
      />
      <main className="flex min-h-0 flex-1 flex-col">
        <SflmaExplorer />
      </main>
      <UserProfileModal
        variant="page"
        open={settingsOpen}
        user={user}
        onClose={() => setSettingsOpen(false)}
        onSaved={async () => {
          await refreshUser();
        }}
      />
    </div>
  );
}
